// Self-contained factory: the same trusted code runs in the spreadsheet iframe
// and the read-only viewer. Formula text is parsed; it is never executed as JS.
export function createSpreadsheetCore() {
    function coords(id) {
        var match=/^\$?([A-Z]+)\$?([1-9]\d*)$/i.exec(String(id));
        if(!match)return null;
        var col=0;for(var i=0;i<match[1].length;i++)col=col*26+match[1].toUpperCase().charCodeAt(i)-64;
        return {col:col-1,row:Number(match[2])};
    }
    function column(index) { var text='';for(var n=index+1;n>0;n=Math.floor((n-1)/26))text=String.fromCharCode(65+(n-1)%26)+text;return text; }
    function number(value) {
        if(typeof value==='number')return Number.isFinite(value)?value:null;
        if(typeof value==='boolean')return value?1:0;
        var text=String(value == null?'':value).trim();
        if(!text || text[0]==="'")return null;
        if(/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?%$/i.test(text))return Number(text.slice(0,-1))/100;
        if(!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(text))return null;
        var parsed=Number(text);return Number.isFinite(parsed)?parsed:null;
    }
    function error(code){throw new Error(code);}
    function numeric(value) {
        if(Array.isArray(value)){if(value.length!==1)error('#VALUE!');value=value[0];}
        if(typeof value==='string' && value[0]==='#')error(value);
        if(value==='' || value==null)return 0;
        var n=number(value);if(n===null)error('#VALUE!');return n;
    }
    function tokenize(formula) {
        var tokens=[],i=0,text=String(formula).trim().replace(/^=/,'');
        while(i<text.length) {
            if(/\s/.test(text[i])){i++;continue;}
            var rest=text.slice(i),match;
            if((match=/^"(?:[^"]|"")*"/.exec(rest))){tokens.push({type:'string',value:match[0].slice(1,-1).replace(/""/g,'"')});i+=match[0].length;}
            else if((match=/^(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?/i.exec(rest))){tokens.push({type:'number',value:Number(match[0])});i+=match[0].length;}
            else if((match=/^#[A-Z0-9/]+[!?]?/.exec(rest))){tokens.push({type:'error',value:match[0]});i+=match[0].length;}
            else if((match=/^\$?[A-Z]+\$?[1-9]\d*\b/i.exec(rest))){tokens.push({type:'ref',value:match[0].toUpperCase().replace(/\$/g,'')});i+=match[0].length;}
            else if((match=/^[A-Z_]+/i.exec(rest))){tokens.push({type:'name',value:match[0].toUpperCase()});i+=match[0].length;}
            else if((match=/^(?:<=|>=|<>|[+\-*/^%(),:;=<>])/.exec(rest))){tokens.push({type:'op',value:match[0]===';'?',':match[0]});i+=match[0].length;}
            else error('#NAME?');
            if(tokens.length>2048)error('#NUM!');
        }
        return tokens;
    }
    function parse(formula) {
        var tokens=tokenize(formula),at=0,depth=0;
        function peek(value){return tokens[at] && tokens[at].value===value;}
        function expect(value){if(!peek(value))error('#ERROR!');at++;}
        function expression(min) {
            if(++depth>100)error('#NUM!');
            var token=tokens[at++],left;if(!token)error('#ERROR!');
            if(token.type==='number' || token.type==='string')left={type:'literal',value:token.value};
            else if(token.type==='error')left={type:'error',value:token.value};
            else if(token.type==='ref') {
                left={type:'ref',value:token.value};
                if(peek(':')){at++;var end=tokens[at++];if(!end || end.type!=='ref')error('#REF!');left={type:'range',start:token.value,end:end.value};}
            } else if(token.value==='+' || token.value==='-')left={type:'unary',op:token.value,right:expression(5)};
            else if(token.value==='('){left=expression(0);expect(')');}
            else if(token.type==='name') {
                if(token.value==='TRUE' || token.value==='FALSE')left={type:'literal',value:token.value==='TRUE'};
                else {expect('(');var args=[];if(!peek(')')){do{args.push(expression(0));if(!peek(','))break;at++;}while(true);}expect(')');left={type:'call',name:token.value,args:args};}
            } else error('#ERROR!');
            while(at<tokens.length) {
                if(peek('%')){at++;left={type:'percent',right:left};continue;}
                var op=tokens[at].value,precedence={'=':1,'<>':1,'<':1,'>':1,'<=':1,'>=':1,'+':2,'-':2,'*':3,'/':3,'^':4}[op];
                if(!precedence || precedence<min)break;
                at++;left={type:'binary',op:op,left:left,right:expression(precedence+1)};
            }
            depth--;return left;
        }
        var tree=expression(0);if(at!==tokens.length)error('#ERROR!');return tree;
    }
    function calculator(cells) {
        var cache=Object.create(null),visiting=new Set(),trees=new Map(),visits=0;
        function read(id) {
            if(Object.prototype.hasOwnProperty.call(cache,id))return cache[id];
            if(visiting.has(id))error('#CIRC!');
            if(visiting.size>250)error('#NUM!');
            visits++;var cell=cells[id] || {},value=cell.value == null?'':cell.value;
            if(cell.format==='text'){cache[id]=String(value);return cache[id];}
            if(typeof value==='string' && value[0]==="'")return value.slice(1);
            if(typeof value==='string' && value.trim()[0]==='=') {
                visiting.add(id);
                try{value=evaluate(value);}catch(e){value=e.message[0]==='#'?e.message:'#ERROR!';}
                visiting.delete(id);
            }
            cache[id]=value;return value;
        }
        function range(start,end) {
            var a=coords(start),b=coords(end),values=[];
            if(!a || !b)error('#REF!');
            var minR=Math.min(a.row,b.row),maxR=Math.max(a.row,b.row),minC=Math.min(a.col,b.col),maxC=Math.max(a.col,b.col);
            if((maxR-minR+1)*(maxC-minC+1)>20000)error('#NUM!');
            for(var r=minR;r<=maxR;r++)for(var c=minC;c<=maxC;c++)values.push(read(column(c)+r));
            return values;
        }
        function run(node) {
            if(node.type==='literal')return node.value;
            if(node.type==='error')error(node.value);
            if(node.type==='ref'){var value=read(node.value);if(typeof value==='string' && value[0]==='#')error(value);return value;}
            if(node.type==='range')return range(node.start,node.end);
            if(node.type==='unary')return (node.op==='-'?-1:1)*numeric(run(node.right));
            if(node.type==='percent')return numeric(run(node.right))/100;
            if(node.type==='binary') {
                var left=run(node.left),right=run(node.right),l,r;
                if(['=','<>','<','>','<=','>='].indexOf(node.op)!==-1){l=number(left);r=number(right);if(l===null || r===null){l=String(left).toLowerCase();r=String(right).toLowerCase();}return node.op==='='?l===r:node.op==='<>'?l!==r:node.op==='<'?l<r:node.op==='>'?l>r:node.op==='<='?l<=r:l>=r;}
                l=numeric(left);r=numeric(right);
                if(node.op==='/'){if(r===0)error('#DIV/0!');return l/r;}
                return node.op==='+'?l+r:node.op==='-'?l-r:node.op==='*'?l*r:Math.pow(l,r);
            }
            var name=node.name,args=node.args;
            if(name==='IF'){if(args.length<2 || args.length>3)error('#VALUE!');return numeric(run(args[0]))?run(args[1]):args[2]?run(args[2]):false;}
            if(name==='IFERROR'){if(args.length!==2)error('#VALUE!');try{return run(args[0]);}catch(e){return run(args[1]);}}
            var values=[],raw=args.map(run);
            raw.forEach(function(value){if(Array.isArray(value))values=values.concat(value);else values.push(value);});
            values.forEach(function(value){if(typeof value==='string' && value[0]==='#')error(value);});
            var nums=values.map(number).filter(function(n){return n!==null;}),sum=nums.reduce(function(a,b){return a+b;},0);
            if(name==='SUM')return sum;
            if(name==='AVERAGE'){if(!nums.length)error('#DIV/0!');return sum/nums.length;}
            if(name==='MIN')return nums.length?Math.min.apply(null,nums):0;
            if(name==='MAX')return nums.length?Math.max.apply(null,nums):0;
            if(name==='COUNT')return nums.length;
            if(name==='COUNTA')return values.filter(function(value){return value!=='' && value!=null;}).length;
            if(name==='ABS' && raw.length===1)return Math.abs(numeric(raw[0]));
            if(name==='SQRT' && raw.length===1){var rad=numeric(raw[0]);if(rad<0)error('#NUM!');return Math.sqrt(rad);}
            if(name==='ROUND' && raw.length===2){var n=numeric(raw[0]),places=numeric(raw[1]);if(!Number.isInteger(places) || Math.abs(places)>15)error('#NUM!');var scale=Math.pow(10,places);return Math.sign(n)*Math.round(Math.abs(n)*scale+Number.EPSILON*Math.abs(n*scale)*2)/scale;}
            if(name==='NPV') {
                if(raw.length<2)error('#VALUE!');var rate=numeric(raw[0]);if(rate===-1)error('#DIV/0!');
                var flows=[];raw.slice(1).forEach(function(value){flows=flows.concat(value);});
                flows=flows.map(number).filter(function(value){return value!==null;});
                return flows.reduce(function(total,value,index){return total+value/Math.pow(1+rate,index+1);},0);
            }
            if(name==='IRR') {
                if(!Array.isArray(raw[0]) || raw.length>2)error('#VALUE!');var cash=raw[0].map(number).filter(function(value){return value!==null;}),guess=raw.length===2?numeric(raw[1]):0.1;
                if(!cash.some(function(v){return v>0;}) || !cash.some(function(v){return v<0;}))error('#NUM!');
                for(var iteration=0;iteration<100;iteration++){
                    if(guess<=-1 || !Number.isFinite(guess))error('#NUM!');var npv=0,derivative=0;
                    for(var t=0;t<cash.length;t++){npv+=cash[t]/Math.pow(1+guess,t);if(t)derivative-=t*cash[t]/Math.pow(1+guess,t+1);}
                    if(Math.abs(npv)<1e-7)return guess;
                    if(Math.abs(derivative)<1e-14)error('#NUM!');guess-=npv/derivative;
                }error('#NUM!');
            }
            error('#NAME?');
        }
        function evaluate(formula){if(!trees.has(formula))trees.set(formula,parse(formula));var value=run(trees.get(formula));if(Array.isArray(value))error('#VALUE!');if(typeof value==='number' && !Number.isFinite(value))error('#NUM!');return value;}
        return {read:read,evaluate:function(formula){try{return evaluate(formula);}catch(e){return e.message[0]==='#'?e.message:'#ERROR!';}},visits:function(){return visits;}};
    }
    function calculate(cells){var calc=calculator(cells),results={};Object.keys(cells).forEach(function(id){if(typeof cells[id].value==='string' && cells[id].value.trim()[0]==='=' && cells[id].format!=='text')results[id]=calc.read(id);});return results;}
    function shift(formula,dc,dr,maxCol,maxRow) {
        return String(formula).replace(/"(?:[^"]|"")*"|(\$?)([A-Z]+)(\$?)([1-9]\d*)/gi,function(match,fixCol,col,fixRow,row){
            if(match[0]==='"')return match;var point=coords(col+row),c=point.col+(fixCol?0:dc),r=point.row+(fixRow?0:dr);
            if(c<0 || r<1 || c>=(maxCol || 16384) || r>(maxRow || 1048576))return '#REF!';
            return (fixCol || '')+column(c)+(fixRow || '')+r;
        });
    }
    function format(cell) {
        cell=cell || {};var type=cell.format || 'general',value=cell.result!==undefined?cell.result:cell.value == null?'':cell.value;
        if(type==='text')return String(cell.value == null?'':cell.value);
        if(typeof value==='boolean')return value?'TRUE':'FALSE';
        if(typeof value==='string' && value[0]==="'")return value.slice(1);
        var n=number(value),places=Math.min(15,Math.max(0,Number.isInteger(cell.decimalPlaces)?cell.decimalPlaces:2));
        if(n!==null) {
            if(type==='number')return n.toFixed(places);
            if(type==='percentage')return (n*100).toFixed(places)+'%';
            if(type==='currency' || type==='accounting')return new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',currencySign:type==='accounting'?'accounting':'standard',minimumFractionDigits:places,maximumFractionDigits:places}).format(n);
            if(type==='general')return cell.decimalPlaces===undefined?String(n):n.toFixed(places);
        }
        if(['shortDate','longDate','time'].indexOf(type)!==-1 && /^\d{4}-\d{2}-\d{2}/.test(String(value))){var date=new Date(value);if(!isNaN(date.getTime()))return type==='shortDate'?date.toLocaleDateString('en-US'):type==='longDate'?date.toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'}):date.toLocaleTimeString('en-US');}
        return String(value);
    }
    function parseTSV(text){
        var rows=[[]],value='',quoted=false;
        for(var i=0;i<text.length;i++){var char=text[i];if(char==='"' && (quoted || !value)){if(quoted && text[i+1]==='"'){value+='"';i++;}else quoted=!quoted;}
            else if(!quoted && (char==='\t' || char==='\n' || char==='\r')){rows[rows.length-1].push(value);value='';if(char!=='\t'){if(char==='\r' && text[i+1]==='\n')i++;rows.push([]);}}
            else value+=char;}
        rows[rows.length-1].push(value);if(rows.length>1 && rows[rows.length-1].length===1 && rows[rows.length-1][0]==='')rows.pop();return rows;
    }
    return {coords:coords,column:column,number:number,calculator:calculator,calculate:calculate,shift:shift,format:format,parseTSV:parseTSV};
}
